import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

export interface ClientSearchResult {
  id: number;
  name: string;
  email: string | null;
  phone_number: string | null;
  city: string | null;
  has_open_lead: boolean;
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;
const MAX_RESULTS = 20;

// PostgREST parses `or=(...)` as a comma-separated list, so a term containing a
// comma or a paren would be read as more filters. `%` and `*` are wildcards.
const sanitize = (term: string) => term.replace(/[,()%*\\]/g, ' ').trim();

/**
 * Debounced people search for pickers, over the `clients_unaccent` view.
 *
 * Matches name (accent-insensitively), email, or phone. Restricted to
 * `is_pickable` — people with a real name or some history — which keeps the
 * anonymous, phone-only rows that inbound calls mint out of the list.
 */
export function useClientSearch(query: string) {
  const [results, setResults] = useState<ClientSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  // Monotonically increasing id so a slow earlier request can't clobber a
  // faster later one.
  const requestId = useRef(0);

  useEffect(() => {
    const trimmed = sanitize(query);

    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const currentRequest = ++requestId.current;

    const timer = setTimeout(() => {
      void (async () => {
        const { data, error } = await supabase
          .from('clients_unaccent')
          .select('id, name, email, phone_number, city, has_open_lead')
          .or(
            `unaccent.ilike.%${trimmed}%,` +
              `email.ilike.%${trimmed}%,` +
              `phone_number.ilike.%${trimmed}%`,
          )
          .eq('is_pickable', true)
          .not('deceased', 'is', true)
          .order('name')
          .limit(MAX_RESULTS);

        // A newer query superseded this one — drop the stale response.
        if (currentRequest !== requestId.current) {
          return;
        }

        if (error) {
          console.error('client search failed:', error);
          setResults([]);
        } else {
          setResults((data as ClientSearchResult[]) ?? []);
        }
        setLoading(false);
      })();
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  return { results, loading };
}
